import React, { useEffect } from "react";
import {
  FaLaptopCode,
  FaMobileAlt,
  FaBullhorn,
  FaUserTie,
  FaLightbulb,
  FaDraftingCompass,
  FaCode,
  FaRocket,
  FaCheckCircle,
} from "react-icons/fa";

export default function Services() {
  useEffect(() => {
    document.title = "Services | Development Solutions";
  }, []);

  return (
    <>
      {/* ✅ HERO SECTION */}
      <section
        className="relative h-[50vh] flex items-center justify-center text-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/1181312/pexels-photo-1181312.jpeg?auto=compress&cs=tinysrgb&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>

        {/* Text content */}
        <div className="relative z-10 px-4 sm:px-6">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            Our Services
          </h1>
          <p
            className="mt-4 text-sm sm:text-base lg:text-lg text-gray-200 max-w-3xl mx-auto"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            We empower businesses with cutting-edge technology solutions that
            transform ideas into scalable digital products.
          </p>
        </div>
      </section>

      {/* ✅ INTRO SECTION */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto h-[40vh] text-center flex flex-col justify-center px-4 sm:px-6 gap-4 sm:gap-6">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            Driving Innovation with{" "}
            <span style={{ color: "#1b75bc" }}>Technology</span>
          </h2>
          <p
            className="text-sm sm:text-base lg:text-lg leading-relaxed max-w-4xl mx-auto"
            style={{ color: "#555", fontFamily: "Montserrat, sans-serif" }}
          >
            At <strong>Development Solutions</strong>, we specialize in
            delivering high-quality software services tailored to your needs.
            From robust web platforms to seamless mobile experiences, we
            leverage the latest technologies to deliver solutions that grow with
            your business.
          </p>
        </div>
      </section>

      {/* ✅ SERVICES GRID */}
      <section className="bg-gray-50">
        <div className="max-w-7xl flex flex-col justify-center h-[90vh] mx-auto px-4 sm:px-6 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-12"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            What We <span style={{ color: "#1b75bc" }}>Offer</span>
          </h2>

          {/* GRID */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                title: "Web Development",
                desc: "Modern, scalable & responsive web solutions that ensure your business stays ahead in the digital age. We build dynamic websites, custom web apps, and e-commerce solutions.",
                img: "https://images.pexels.com/photos/3861964/pexels-photo-3861964.jpeg?auto=compress&cs=tinysrgb&w=800",
                icon: <FaLaptopCode size={24} style={{ color: "#1b75bc" }} />,
              },
              {
                title: "Mobile Apps",
                desc: "Seamless and engaging mobile applications for iOS and Android. From UI/UX design to development, we craft apps that users love to engage with.",
                img: "https://images.pexels.com/photos/1181676/pexels-photo-1181676.jpeg?auto=compress&cs=tinysrgb&w=800",
                icon: <FaMobileAlt size={24} style={{ color: "#1b75bc" }} />,
              },
              {
                title: "Digital Marketing",
                desc: "Grow your online presence with data-driven strategies. We handle SEO, social media marketing, paid ads, and content creation to drive real business results.",
                img: "https://images.pexels.com/photos/3184460/pexels-photo-3184460.jpeg?auto=compress&cs=tinysrgb&w=800",
                icon: <FaBullhorn size={24} style={{ color: "#1b75bc" }} />,
              },
              {
                title: "Technical Consulting",
                desc: "Expert advice on technology stack, cloud solutions, and software scalability. We guide you to make the right tech decisions for long-term success.",
                img: "https://images.pexels.com/photos/3184357/pexels-photo-3184357.jpeg?auto=compress&cs=tinysrgb&w=800",
                icon: <FaUserTie size={24} style={{ color: "#1b75bc" }} />,
              },
            ].map((service, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden"
              >
                {/* Image with hover zoom */}
                <div className="overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.title}
                    className="h-36 sm:h-48 w-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {/* Card Text */}
                <div className="p-3 sm:p-5 text-left">
                  <div className="mb-2 sm:mb-3">{service.icon}</div>
                  <h3
                    className="text-base sm:text-lg font-bold mb-2"
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      color: "#414042",
                    }}
                  >
                    {service.title}
                  </h3>
                  <p
                    className="text-xs sm:text-sm leading-relaxed"
                    style={{
                      color: "#555",
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    {service.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ HOW WE WORK SECTION */}
      <section className="bg-white">
        <div className="max-w-7xl flex flex-col justify-center h-[90vh] mx-auto px-4 sm:px-6 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-12"
            style={{ color: "#414042", fontFamily: "Montserrat, sans-serif" }}
          >
            Our Proven <span style={{ color: "#1b75bc" }}>Process</span>
          </h2>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {[
              {
                step: "Discover",
                desc: "We understand your goals, challenges & vision to ensure alignment.",
                icon: <FaLightbulb size={32} style={{ color: "#1b75bc" }} />,
              },
              {
                step: "Design",
                desc: "Creating UI/UX that's clean, modern & user-focused for better engagement.",
                icon: (
                  <FaDraftingCompass size={32} style={{ color: "#1b75bc" }} />
                ),
              },
              {
                step: "Develop",
                desc: "Agile development with the latest tech stack for scalable products.",
                icon: <FaCode size={32} style={{ color: "#1b75bc" }} />,
              },
              {
                step: "Deliver",
                desc: "On-time delivery with ongoing support, monitoring & future upgrades.",
                icon: <FaRocket size={32} style={{ color: "#1b75bc" }} />,
              },
            ].map((phase, idx) => (
              <div
                key={idx}
                className="p-4 sm:p-6 bg-white rounded-lg shadow hover:shadow-md transition"
              >
                <div className="mb-3 sm:mb-4 flex justify-center">
                  {phase.icon}
                </div>
                <h3
                  className="text-lg sm:text-xl font-bold mb-2"
                  style={{
                    color: "#1b75bc",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  {phase.step}
                </h3>
                <p
                  className="text-xs sm:text-sm leading-relaxed"
                  style={{
                    color: "#414042",
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  {phase.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ FINAL CTA */}
      <section
        className="relative py-16 sm:py-20 lg:py-24 text-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        <div className="relative z-10 px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            Let's Build Your Next Big Idea
          </h2>
          <p
            className="mt-4 text-sm sm:text-base lg:text-lg text-gray-200 max-w-xl mx-auto"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            From concept to execution, we're your technology partner.
          </p>
          <a
            href="/contact"
            className="mt-6 inline-block px-6 sm:px-8 py-3 sm:py-4 rounded-md text-white font-bold"
            style={{
              backgroundColor: "#1b75bc",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            Get in Touch
          </a>
        </div>
      </section>
    </>
  );
}
